import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import useAlert from "./useAlert";
import useAxiosPrivate from "./useAxiosPrivate";
import useDidMountEffect from "./useDidMountEffect";
import useObject from "./useObject";

type PartOf<T> = {[K in keyof T]? : T[K]}

interface props<T> {
  onDataChange ?: (data : T)=>void,
  getUrl : string | {[x in keyof T ] : string},
  postUrl : string,
  initialData : T,
}
const useMetaManager = <T extends {},>({initialData, onDataChange, getUrl, postUrl}:props<T>)=>{

  const [data, _setPartialData, setData, _intentData] = useObject<T>(initialData);
  const dataRef = useRef(data);
  dataRef.current = data;

  const [prev, setPrev] = useState<T>(initialData); //for checking if data changed
  const [loaded, setLoaded] = useState(true);
  const {setAlert} = useAlert()

  const axios = useAxiosPrivate();
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [isPosting, setIsPosting] = useState(false);

  async function getData(){
    if(typeof getUrl === 'string'){
      const response = await axios.get<T>(getUrl)
      setData(response.data);
    } else {
      const responses = await Promise.all(Object.keys(getUrl).map(async (key)=>({ [key] : (await axios.get<T>(getUrl[key as keyof T])).data})))
      const data = responses.reduce((acc, cur)=>({...acc, ...cur}), {} as T)
      //@ts-ignore
      setData(data);
    }
    setLoaded(false)
  }
  //something wrong with saving module, it triggered on init
  useEffect(()=>{
    getData();
  }, [])

  useEffect(()=>{
    onDataChange && onDataChange(data);
  }, [data])

  const changeData = (func : any, changedData : keyof T)=>{
    if (timer) {
      clearTimeout(timer);
    }

    const newTimer = setTimeout(() => {
      setIsPosting(true);
      const postData = {[`${String(changedData)}`] : dataRef.current[changedData]}
      axios.post(postUrl, postData).then((res)=>{
        setIsPosting(false);
        setTimer(null);
      }).catch((e)=>{
        setAlert('error', 'Error while saving data');
        console.dir(e)
      })
    }, 1000);

    setTimer(newTimer);
    
    func();
  }

  const intentData = (func : (prev:T)=>T, changedData : keyof T) => changeData(()=>_intentData(func), changedData)
  const setPartialData = (data : PartOf<T>, changedData : keyof T) => changeData(()=>_setPartialData(data), changedData)


  return {data, intentData, isPosting, loaded, setPartialData}
}

export default useMetaManager;